import type { PbApiOptions } from '~/composables/api';
import type { BaseAPIResponse, Meta } from '~/types/api';
import { ApiError } from '~/utils/errors';

/**
 * Everything a service call needs on top of the transport options: which statuses count as
 * success, and the one sentence to fall back on when the envelope carried no message.
 */
export type PbRequestOptions = PbApiOptions & {
    /** Status (or statuses) this call treats as success. Defaults to 200. */
    expect?: number | number[];
    /** Message for the thrown `ApiError` when the response carried none. */
    failure: string;
};

/**
 * The one request path every service call takes.
 *
 * `usePbApi` resolves a non-2xx into `{ data, status, message }` rather than throwing, so a
 * refusal and a transport failure arrive here by two different routes; both leave as an
 * `ApiError` carrying the status the API chose and, when the body held one, its machine-readable
 * `reason`. Doing that in one place is what stops a caller from having to guess which shape of
 * failure a given service method hands it.
 */
async function perform<T>(url: string, options: PbRequestOptions): Promise<T | null> {
    const { expect = 200, failure, ...fetchOptions } = options;

    let answer: { data: T; status: number; message: string; retryAfter: number | null };

    try {
        answer = await usePbApi<T>(url, fetchOptions);
    } catch (error) {
        throw ApiError.fromFetchError(error);
    }

    const accepted = Array.isArray(expect) ? expect : [expect];

    if (!accepted.includes(answer.status)) {
        throw ApiError.fromEnvelope(answer.status, answer.message, answer.data, failure, answer.retryAfter);
    }

    return answer.data ?? null;
}

/** A call whose answer is a `{ data: … }` envelope: resolves to the unwrapped payload. */
export async function pbRequest<T>(url: string, options: PbRequestOptions): Promise<T> {
    const envelope = await perform<BaseAPIResponse<T>>(url, options);

    if (envelope?.data === undefined || envelope.data === null) {
        throw new ApiError(options.failure, 404);
    }

    return envelope.data;
}

/**
 * A page of a list: the rows an index answers under `data`, and its pagination under `meta`.
 *
 * A page that arrives without either is a broken contract, never an empty list, so it throws: an
 * empty ledger or a total of nothing would tell the reader there is nothing there.
 */
export async function pbRequestPage<T>(url: string, options: PbRequestOptions): Promise<{ data: T[]; meta: Meta }> {
    const envelope = await perform<BaseAPIResponse<T[]>>(url, options);

    if (!Array.isArray(envelope?.data) || !envelope.meta) {
        throw new ApiError(options.failure, 500);
    }

    return { data: envelope.data, meta: envelope.meta };
}

/** A call whose body is the answer itself — a bare payload, a blob, or an envelope a caller reads whole. */
export async function pbRequestRaw<T>(url: string, options: PbRequestOptions): Promise<T> {
    const body = await perform<T>(url, options);

    if (body === null) {
        throw new ApiError(options.failure, 404);
    }

    return body;
}

/** A call that answers with nothing worth reading. */
export async function pbRequestVoid(url: string, options: PbRequestOptions): Promise<void> {
    await perform<unknown>(url, options);
}
